import moment from 'moment/moment';
import { formatSessionClockWithDate } from '../helper';

/**
 * Time window: allow when now >= start - 30m and now < end.
 * @returns {null|{ canJoin: boolean, reason: 'too_early'|'too_late'|null }}
 */
export function getCanJoinByTime(sessionInfo, nowMs = Date.now()) {
  if (!sessionInfo?.startTime || !sessionInfo?.endTime) {
    return null;
  }

  const now = moment(nowMs);
  const startTime = moment(sessionInfo.startTime);
  const endTime = moment(sessionInfo.endTime);
  const preJoinTime = startTime.clone().subtract(30, 'minutes');

  if (now.isBefore(preJoinTime)) {
    return { canJoin: false, reason: 'too_early' };
  }

  if (now.isSameOrAfter(endTime)) {
    return { canJoin: false, reason: 'too_late' };
  }

  return { canJoin: true, reason: null };
}

/**
 * Derive flags from session.state + infoExtra.state (precedence like handleCaseForSenior).
 */
export function deriveJoinStateFlags(sessionInfo) {
  if (!sessionInfo) {
    return {
      isEndedSession: false,
      isNotStartedSession: false,
      isProcessingSession: false,
    };
  }

  const state = sessionInfo.state;
  const ex = sessionInfo.infoExtra?.state;

  const isEnded = state === 'is_ended' || ex === 'is_ended';
  const isProcessing = state === 'is_processing' || ex === 'is_processing';
  const isNotStarted =
    state === 'is_not_started_5_hours' ||
    state === 'is_not_started' ||
    !state ||
    ex === 'is_not_started';

  if (isProcessing) {
    return { isEndedSession: false, isNotStartedSession: false, isProcessingSession: true };
  }
  if (isEnded) {
    return { isEndedSession: true, isNotStartedSession: false, isProcessingSession: false };
  }
  if (isNotStarted) {
    return { isEndedSession: false, isNotStartedSession: true, isProcessingSession: false };
  }
  return { isEndedSession: false, isNotStartedSession: false, isProcessingSession: false };
}

/**
 * Rule valid — cùng ý session-detail (readyJoinSession / join):
 * 1) Có start+end: chỉ theo cửa sổ [start−30m, end).
 * 2) Không đủ start/end: chặn nếu đã kết thúc; hoặc chưa diễn ra và API tường minh canPreJoin === false.
 *
 * Lưu ý: không dùng !canPreJoin vì undefined sẽ bị hiểu như “không được prejoin” → nút Vào phiên tắt sai trên tab mới
 * khi infoExtra thiếu field hoặc merge không có canPreJoin.
 */
export function evaluateJoinEligibility(sessionInfo, nowMs = Date.now()) {
  const timeCheck = getCanJoinByTime(sessionInfo, nowMs);
  const flags = deriveJoinStateFlags(sessionInfo);

  if (timeCheck && !timeCheck.canJoin) {
    return {
      ok: false,
      timeReason: timeCheck.reason,
      flags,
    };
  }

  if (
    !timeCheck &&
    (flags.isEndedSession ||
      (flags.isNotStartedSession && sessionInfo?.infoExtra?.canPreJoin === false))
  ) {
    return {
      ok: false,
      timeReason: null,
      flags,
    };
  }

  return { ok: true, timeReason: null, flags };
}

export function formatJoinTimeLabels(sessionInfo) {
  if (!sessionInfo) {
    return { startTimeNotDayText: '', endTimeText: '' };
  }

  const startTimeNotDayText = formatSessionClockWithDate(sessionInfo.startTime);
  const endTimeText = formatSessionClockWithDate(sessionInfo.endTime, sessionInfo.startTime);

  return { startTimeNotDayText, endTimeText };
}

export function buildJoinRejectionToastLines(I18n, sessionInfo, evaluation) {
  const { startTimeNotDayText, endTimeText } = formatJoinTimeLabels(sessionInfo);
  const { timeReason, flags } = evaluation;

  const showEnded = timeReason === 'too_late' || flags.isEndedSession;
  if (showEnded) {
    return {
      title: I18n.t('session_end_at_time', { time: endTimeText }),
      body: I18n.t('session_explore'),
    };
  }

  return {
    title: I18n.t('session_start_at_time', { time: startTimeNotDayText }),
    body: I18n.t('session_started_yet'),
  };
}

/** Same guard as session-detail joinSession before verifyJoinSession */
export function canProceedToVerifyJoin(sessionInfo) {
  const flags = deriveJoinStateFlags(sessionInfo);
  const disabled = sessionInfo?.infoExtra?.isDisable;
  return flags.isProcessingSession || !disabled;
}
